import { useRef, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import useSWR from 'swr'

import { CaseStudyFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { isDevelopment } from '@/utils/isDevelopment'

import { Warning } from '@/components/Warning'

import { useCaseStudyFilters } from '../CaseStudyFilters/useCaseStudyFilters'

type Props = {
	className?: string
	filterButtonLabel?: string
	industryColumnHeader?: string
	topicColumnHeader?: string
	competitorColumnHeader?: string
	applyButtonLabel?: string
}

type UpdateQueryParamsProps = {
	industry: { title: string; slug: string } | null
	topics: { title: string; slug: string }[]
	competitors: { title: string; slug: string }[]
}
let timerId: NodeJS.Timeout | undefined = undefined
export function CaseStudySideBar({
	className,
	filterButtonLabel,
	industryColumnHeader,
	topicColumnHeader,
	competitorColumnHeader,
	applyButtonLabel
}: Props) {
	const { locale, isPreview, query, push } = useRouter()

	const {
		industries,
		topics,
		competitors,
		appliedIndustry,
		appliedTopics,
		appliedCompetitors,
		activeCompetitors,
		activeIndustry,
		activeTopics,
		setActiveIndustry,
		setActiveTopics,
		setActiveCompetitors
	} = useCaseStudyFilters()

	const [showMenu, setShowMenu] = useState(false)
	const showRef = useRef<HTMLDivElement>(null)

	useClickAway(showRef, () => setShowMenu(false))
	const { error } = useSWR(
		serializeCacheKey('caseStudyFeedTotal', {
			locale,
			industry: appliedIndustry?.title,
			topics: appliedTopics.map((activeTopic) => activeTopic.title),
			competitors: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
		}),
		() =>
			client({ preview: isPreview }).request(CaseStudyFeedDocument, {
				where: {
					...(appliedIndustry?.title ? { industriesList: { name: appliedIndustry.title } } : {}),
					...(appliedTopics.length > 0
						? { topicsList: { name_in: appliedTopics.map((activeTopic) => activeTopic.title) } }
						: {}),
					...(appliedCompetitors.length > 0
						? {
								competitorsList: {
									name_in: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
								}
						  }
						: {}),
					status: true
				},
				limit: 0,
				skip: 0,
				locale,
				preview: isPreview
			})
	)

	function updateQueryParams({ industry, topics, competitors }: UpdateQueryParamsProps) {
		if (timerId) {
			clearTimeout(timerId)
		}
		timerId = setTimeout(async () => {
			push(
				{
					query: {
						...query,
						industries: industry?.slug ?? [],
						topics: topics.map((topic) => topic.slug) ?? [],
						competitors: competitors.map((competitor) => competitor.slug) ?? []
					}
				},
				undefined,
				{ shallow: true }
			)
		}, 500)
	}

	if (error) {
		if (isDevelopment()) {
			return <Warning>{error?.message || 'An error occurred'}</Warning>
		}
		return null
	}

	return (
		<div className="w-full">
			<div className={clsx(showMenu && 'fixed inset-0 top-0 z-30', className)}>
				<FilterMenu showMenu={showMenu} showRef={showRef}>
					<FilterOptions title={industryColumnHeader ?? 'By Industry'}>
						{industries.length > 0 && (
							<ul className="flex flex-col items-start gap-y-1.5 mt-5">
								{industries.map((industry, i) => (
									<li key={industry.slug}>
										<input
											type="radio"
											data-slug={industry.slug}
											data-testid="industry-filter"
											id={`industry-filter-${industry.slug}-${i}`}
											onChange={() => {
												setActiveIndustry(industry)
												updateQueryParams({
													industry,
													topics: activeTopics,
													competitors: activeCompetitors
												})
											}}
											checked={activeIndustry?.slug === industry.slug}
											className={clsx('filter-option', 'outline-bc-blue w-5 h-5')}
										/>
										<label
											htmlFor={`industry-filter-${industry.slug}-${i}`}
											className="pl-2 text-gray-600 text-xs"
										>
											{industry.title}
										</label>
									</li>
								))}
							</ul>
						)}
					</FilterOptions>

					<FilterOptions title={topicColumnHeader ?? 'By Topic'}>
						{topics.length > 0 && (
							<div className="flex flex-col max-md:px-7.5">
								<ul className="flex flex-col items-start gap-y-1.5 mt-5">
									{topics.map((topic, i) => (
										<li key={`topic-filter-${topic.slug}-${i}`}>
											<input
												type="checkbox"
												id={`topic-filter-${topic.slug}-${i}`}
												data-testid="topic-filter"
												onChange={() => {
													setActiveTopics((prev) => {
														if (prev.some((activeTopic) => activeTopic.slug === topic.slug)) {
															const filteredTopics = prev.filter(
																(activeTopic) => activeTopic.slug !== topic.slug
															)
															updateQueryParams({
																industry: activeIndustry,
																topics: filteredTopics,
																competitors: activeCompetitors
															})
															return filteredTopics
														} else {
															updateQueryParams({
																industry: activeIndustry,
																topics: [...prev, topic],
																competitors: activeCompetitors
															})
															return [...prev, topic]
														}
													})
												}}
												checked={activeTopics.some(
													(activeTopic) => activeTopic.slug === topic.slug
												)}
												className={clsx('filter-option', 'outline-bc-blue w-5 h-5')}
											/>
											<label
												htmlFor={`topic-filter-${topic.slug}-${i}`}
												className="pl-2 text-gray-600 text-xs"
											>
												{topic.title}
											</label>
										</li>
									))}
								</ul>
							</div>
						)}
					</FilterOptions>

					<FilterOptions title={competitorColumnHeader ?? 'By Migration Story'}>
						{competitors.length > 0 && (
							<div className="flex flex-col max-md:px-7.5">
								<ul className="flex flex-col items-start gap-y-1.5 mt-5">
									{competitors.map((competitor, i) => (
										<li key={`competitor-filter-${competitor.slug}-${i}`}>
											<input
												type="checkbox"
												data-testid="competitor-filter"
												id={`competitor-filter-${competitor.slug}-${i}`}
												onChange={() => {
													setActiveCompetitors((prev) => {
														if (
															prev.some(
																(activeCompetitor) => activeCompetitor.slug === competitor.slug
															)
														) {
															const filteredCompetitors = prev.filter(
																(activeCompetitor) => activeCompetitor.slug !== competitor.slug
															)
															updateQueryParams({
																industry: activeIndustry,
																topics: activeTopics,
																competitors: filteredCompetitors
															})
															return filteredCompetitors
														} else {
															updateQueryParams({
																industry: activeIndustry,
																topics: activeTopics,
																competitors: [...prev, competitor]
															})
															return [...prev, competitor]
														}
													})
												}}
												checked={activeCompetitors.some(
													(activeCompetitor) => activeCompetitor.slug === competitor.slug
												)}
												className="filter-option outline-bc-blue w-5 h-5"
											/>
											<label
												htmlFor={`competitor-filter-${competitor.slug}-${i}`}
												className="pl-2 text-gray-600 text-xs"
											>
												{competitor.title}
											</label>
										</li>
									))}
								</ul>
							</div>
						)}
					</FilterOptions>
				</FilterMenu>
			</div>
		</div>
	)
}

type FilterOptionProps = {
	title: string
	children: React.ReactNode
}

const FilterOptions = ({ title, children }: FilterOptionProps) => {
	return (
		<div data-testid={`Filter-${title}`} className="text-gray-500 py-6">
			<h5 className="text-[17px] font-medium uppercase text-bc-black ">{title}</h5>
			{children}
		</div>
	)
}

type FilterMenuProps = {
	showMenu: boolean
	showRef: React.RefObject<HTMLDivElement>
	children: React.ReactNode
}

const FilterMenu = ({ showMenu, showRef, children }: FilterMenuProps) => {
	return (
		<>
			<div
				className={clsx(
					showMenu ? 'translate-x-0 z-30' : '-translate-x-full',
					'w-full max-w-[600px] mr-6 absolute top-0 left-0 md:relative md:translate-x-0 transition-all'
				)}
			>
				<div
					className="z-30 w-4/5 md:w-full h-screen md:h-full p-6 md:p-1 bg-white md:bg-transparent overflow-y-auto"
					ref={showRef}
				>
					{children}
				</div>
			</div>

			{/* Mobile Filter Overlay */}
			<div
				className={clsx(
					showMenu ? 'block' : 'hidden',
					'opacity-50 z-20 top-0 w-full md:hidden h-screen bg-bc-black absolute right-0 transition-all'
				)}
			/>
		</>
	)
}
